import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useOtas, useDeleteOta } from 'hooks/api/useOta';
import type { Ota, OtaSearchRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import OtaModal from 'components/modals/modules/ota-module/OtaModal';
import OtaViewModal from 'components/modals/modules/ota-module/OtaViewModal';

type ViewMode = 'list' | 'grid';

const statusOptions: SelectOption[] = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const commissionTypeFilterOptions: SelectOption[] = [
  { value: '', label: 'All Commission Types' },
  { value: 'Percentage', label: 'Percentage' },
  { value: 'Flat Amount', label: 'Flat Amount' },
];

interface OtaMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const OtaMasterScreen: React.FC<OtaMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<OtaSearchRequest>({ page: 1, pageSize: 25 });
  const { data, pagination, refetch } = useOtas(queryParams);
  const { deleteOta, loading: isDeleting } = useDeleteOta();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [filterCommissionType, setFilterCommissionType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Ota | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleView = useCallback((ota: Ota) => {
    const viewId = tabId ? `${tabId}-view-modal-${ota.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(ota);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((otaId: string) => {
    const ota = data.find(o => o.id === otaId);
    if (ota) {
      setSelectedRecord(ota);
      setViewModalOpen(true);
    }
  }, [data]);

  const handleEdit = useCallback((ota: Ota) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${ota.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(ota);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((ota: Ota) => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(ota);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((ota: Ota) => {
    setSelectedRecord(ota);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteOta(selectedRecord.id, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.otaCode}" deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteOta, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, recordId?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (recordId) {
      const ota = data.find(o => o.id === recordId);
      if (ota) setSelectedRecord(ota);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data]);

  const tableColumns: SyutGridColumn<Ota>[] = useMemo(() => [
    { id: 'otaCode', header: 'OTA Code', accessorKey: 'otaCode', size: 130, enableSorting: true },
    { id: 'otaName', header: 'OTA Name', accessorKey: 'otaName', size: 200, enableSorting: true },
    {
      id: 'contactPerson', header: 'Contact Person', accessorKey: 'contactPerson', size: 160, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'phone', header: 'Phone', accessorKey: 'phone', size: 140,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'email', header: 'Email', accessorKey: 'email', size: 200,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'commissionType', header: 'Commission Type', accessorKey: 'commissionType', size: 140, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    {
      id: 'commissionPercent', header: 'Commission %', accessorKey: 'commissionPercent', size: 120, enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as number | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val != null ? `${val}%` : '-'}</span>;
      },
    },
    {
      id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((ota: Ota): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(ota) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(ota) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(ota) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(ota), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({ id: 'add', icon: <IconSystem.add size={18} />, label: 'Add OTA', onClick: handleAddNew, tooltip: 'Add New OTA', variant: 'icon' });
    }
    buttons.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' });
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      commissionType: filterCommissionType || undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterCommissionType, filterStatus]);

  const handleFilterClear = useCallback(() => {
    setFilterCommissionType('');
    setFilterStatus('');
    setQueryParams(prev => ({
      ...prev,
      commissionType: undefined,
      includeInactive: undefined,
      page: 1,
    }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="OTA Master"
        description="Manage OTA (Online Travel Agency) partners"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : <IconSystem.link size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search OTAs...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          appliedCount: [filterCommissionType, filterStatus].filter(Boolean).length,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No OTAs found"
              frozenLeftColumns={['otaCode']}
              frozenRightColumns={['actions']}
            />
          )}

          {viewMode === 'grid' && (
            <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '1rem', display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1.5rem', flex: 1, overflow: 'auto', alignContent: 'start',
              }}>
                {data.length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    <IconSystem.link size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                    <p>No OTAs found</p>
                  </div>
                ) : (
                  data.map((ota) => (
                    <div key={ota.id} style={{
                      background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                      borderRadius: '0.75rem', padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                        {ota.otaCode}
                      </div>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
                        {ota.otaName}
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
                        <div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Commission</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {ota.commissionPercent != null ? `${ota.commissionPercent}%` : '-'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Status</div>
                          <span className={`badge badge-${ota.isActive ? 'active' : 'inactive'}`}>
                            {ota.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => handleView(ota)}>View</button>
                        <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => handleEdit(ota)}>Edit</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Commission Type"
                options={commissionTypeFilterOptions}
                value={filterCommissionType}
                onChange={(val: string | string[] | null) => setFilterCommissionType((val as string) || '')}
                placeholder="Select commission type..."
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={statusOptions}
                value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
            <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
          </div>
        </div>}
      </div>

      <OtaViewModal
        isOpen={viewModalOpen}
        ota={selectedRecord}
        onClose={() => setViewModalOpen(false)}
        onEdit={(ota) => handleEdit(ota)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      <OtaModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.otaCode}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default OtaMasterScreen;
